/**
 * The readiness cut, previewed on the device (D148; a2-port-map §3: "keep
 * `cutSets` only for the readiness preview").
 *
 * ── WHY A LOCAL COPY EXISTS AT ALL ─────────────────────────────────────────
 * The server's `adjustments` arrive only when `POST /me/sessions` lands, and
 * offline that may be an hour later. Until then the session runs on this
 * preview; once the start has synced, the store replaces it with the server's
 * list (`adjustments-sync.ts`) and `liveWorkout` reads that instead.
 *
 * The rule restates `@repo/training`'s `prescription/readiness.ts`
 * (`todayAdjustments`), which the app cannot import (D118): poor sleep cuts
 * 20 %, high stress 10 %, only the larger cut applies and carries its own
 * reason key, the count rounds DOWN with a floor of one, an item whose count
 * does not move gets no entry, and an item with no count (`targetSets: null`)
 * is never cut.
 *
 * It also fills `cutPct` and `rirIncrease`, the two fields the server now
 * sends, so ONE render path serves both the preview and the server's answer
 * (`reasonText`). They mirror the engine's `READINESS_POOR_SLEEP_CUT_PCT`,
 * `READINESS_HIGH_STRESS_CUT_PCT` and `READINESS_POOR_SLEEP_RIR_INCREASE`,
 * and they are read only until `POST /me/sessions` answers — from then on
 * `adjustments-sync.ts` replaces the whole list with the server's, whose
 * figures are the ones that actually applied. `readinessReasonParams` keeps
 * no copy of them any more; this file is the last one.
 */
import type {
  SessionAdjustment,
  SessionReadiness,
} from "@repo/contract/training";

interface Cut {
  readonly pct: number;
  readonly reasonKey: string;
}

const POOR_SLEEP: Cut = { pct: 20, reasonKey: "readiness.poor_sleep" };
const HIGH_STRESS: Cut = { pct: 10, reasonKey: "readiness.high_stress" };

/**
 * `READINESS_POOR_SLEEP_RIR_INCREASE` — the one reason that also moves the
 * reserve, and `0` for every other. A function rather than a field on `Cut`
 * so the preview's shape stays the two numbers the screen model publishes
 * (`readiness-model.ts`), and so the readiness screen can ask for it by key
 * alone.
 *
 * The engine keys this on the ANSWER (`readiness.sleep === "poor"`), not on
 * the cut that won, and the two agree only because the poor night's 20 % is
 * the larger of the two cuts — so a poor night is always the reason key. If
 * the percentages ever cross, this has to move to the answer.
 */
export function previewRirIncrease(reasonKey: string): number {
  return reasonKey === POOR_SLEEP.reasonKey ? 1 : 0;
}

/** The one cut that applies: the larger, the poor night first on a tie. */
export function applicableCut(readiness: SessionReadiness | null): Cut | null {
  if (readiness === null) {
    return null;
  }
  const cuts: Cut[] = [];
  if (readiness.sleep === "poor") {
    cuts.push(POOR_SLEEP);
  }
  if (readiness.stress === "high") {
    cuts.push(HIGH_STRESS);
  }
  let applicable: Cut | null = null;
  for (const cut of cuts) {
    if (applicable === null || cut.pct > applicable.pct) {
      applicable = cut;
    }
  }
  return applicable;
}

/** A percentage off a set count, rounded down, never below one. */
export function cutSetsToday(sets: number, pct: number): number {
  return Math.max(1, Math.floor(sets * (1 - pct / 100)));
}

export interface PreviewItem {
  readonly itemId: string;
  readonly targetSets: number | null;
}

/** What the server will most likely answer, in its own shape. */
export function previewAdjustments(
  items: readonly PreviewItem[],
  readiness: SessionReadiness | null
): SessionAdjustment[] {
  const cut = applicableCut(readiness);
  if (cut === null) {
    return [];
  }
  const adjustments: SessionAdjustment[] = [];
  for (const item of items) {
    if (item.targetSets === null) {
      continue;
    }
    const targetSets = cutSetsToday(item.targetSets, cut.pct);
    if (targetSets !== item.targetSets) {
      adjustments.push({
        cutPct: cut.pct,
        itemId: item.itemId,
        reasonKey: cut.reasonKey,
        rirIncrease: previewRirIncrease(cut.reasonKey),
        targetSets,
      });
    }
  }
  return adjustments;
}
